import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';
import SubTabs from '../../components/SubTabs/SubTabs';
import useDocumentTitle from '../../components/Student360/useDocumentTitle';
import Students from '../Students/Students';
import StudentsDirectory from '../StudentsDirectory/StudentsDirectory';
import StudentStatusDashboard from '../StudentStatus/StudentStatusDashboard';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../i18n/I18nContext';
import './StudentsHub.css';

/**
 * Students hub — the one "O'quvchilar" destination, three tabs:
 *   • Directory  — every student the signed-in staff member may open, across courses, with last
 *                  seen, last login and the current lesson (the default; /students)
 *   • By course  — enrollment roster per course (/students?tab=courses)
 *   • AI risk    — the AI risk score of the signed-in teacher's students, formerly /mentor/students
 *                  (/students?tab=at-risk)
 * (Redesign 2026, Phase 5; the Directory is Student 360.)
 *
 * The last two read routes only an ADMIN, TEACHER or MENTOR account may call (the course list and
 * /mentor/students answer 403 to the other staff), so those accounts are not offered the tabs.
 */
const TABS = [
  { key: 'directory', labelKey: 'student360.tabDirectory' },
  { key: 'courses', labelKey: 'student360.tabByCourse', teachersOnly: true },
  { key: 'at-risk', labelKey: 'student360.tabAiRisk', teachersOnly: true },
];

// Other spellings of a tab that links and bookmarks use: `all` is what the By-course tab was called
// before the Directory became the first tab.
const TAB_ALIASES = { all: 'courses', 'by-course': 'courses' };

function activeTab(params, allowed) {
  const raw = params.get('tab');
  const wanted = TAB_ALIASES[raw] || raw;
  const tab = TABS.find((item) => item.key === wanted);
  return tab && allowed.some((item) => item.key === tab.key) ? tab.key : 'directory';
}

function StudentsHub() {
  const { t } = useI18n();
  const { isAdminOrTeacher } = usePermissions();
  const [params, setParams] = useSearchParams();
  useDocumentTitle(t('student360.hub.title'));
  const offered = TABS.filter((tab) => !tab.teachersOnly || isAdminOrTeacher());
  const active = activeTab(params, offered);

  // Every tab keeps its own filters in the URL, so changing tab starts the next one clean.
  const setTab = (key) => {
    const next = new URLSearchParams();
    if (key !== 'directory') next.set('tab', key);
    setParams(next, { replace: true });
  };

  const tabs = offered.map((tab) => ({ key: tab.key, label: t(tab.labelKey) }));

  return (
    <div className="stuhub">
      {/* The one page header: no eyebrow on a top-level page; the tabs below are the sub-navigation.
          The tabs are embedded pages and print no title of their own. */}
      <div className="page-head">
        <div className="page-head-main">
          <span className="page-head-icon" aria-hidden="true"><GraduationCap /></span>
          <div>
            <h1 className="page-title">{t('student360.hub.title')}</h1>
            <p className="page-sub">{t('student360.hub.subtitle')}</p>
          </div>
        </div>
      </div>

      {tabs.length > 1 && (
        <SubTabs
          className="stuhub-tabs"
          tabs={tabs}
          active={active}
          onChange={setTab}
          ariaLabel={t('student360.hub.tabsLabel')}
        />
      )}

      <div className="stuhub-panel" key={active}>
        {active === 'directory' && <StudentsDirectory />}
        {active === 'courses' && <Students embedded />}
        {active === 'at-risk' && <StudentStatusDashboard embedded />}
      </div>
    </div>
  );
}

export default StudentsHub;
